import React, { useState } from 'react';
import { DropdownFilter } from './DropdownFilter';
import { Button } from '@/components/ui/button';
import { AllFiltersDrawer } from './AllFiltersDrawer';
import { isFilterSelected, isInitialState } from './utils';
import { cn } from '@/lib/utils';
import { FilterOption, FilterData, FiltersValues } from './types';
import useScreenSize from '@/hooks/shared/useScreenSize';
import { BarsFilterIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';

interface FiltersGroupProps {
  currentFiltersData: FilterData[];
  initialFiltersValues: FiltersValues;
  currentFiltersValues: FiltersValues;
  allFiltersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  drawerCloseBtnText?: string;
  customCounterRender?: (count: number) => React.ReactNode;
  customItemRender?: (option: FilterOption) => React.ReactNode;
  hideQuickFiltersResolution?: number;
}

export const FiltersGroup = ({
  currentFiltersData,
  initialFiltersValues,
  currentFiltersValues,
  allFiltersValues,
  handleFiltersValuesChange,
  drawerCloseBtnText,
  customItemRender,
  hideQuickFiltersResolution,
}: FiltersGroupProps) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const screenSize = useScreenSize();

  const isSmallScreen = screenSize.width < (hideQuickFiltersResolution || 1440);

  const handleDrawerOpenChange = (isVisible: boolean) => {
    setIsDrawerVisible(isVisible);
  };

  const quickFilters = currentFiltersData.filter((item) => item.placement === 'quick');
  const additionalFilters = currentFiltersData.filter((item) => item.placement === 'additional');

  const isAnyFilterSelected = (filters: FilterData[]) => {
    let returnValue = false;
    filters.forEach((filter: FilterData) => {
      if (isFilterSelected(allFiltersValues, currentFiltersValues, currentFiltersData, filter.filterLabel)) {
        returnValue = true;
      }
    });
    return returnValue;
  };

  const handleResetClick = () => {
    handleFiltersValuesChange(initialFiltersValues);
  };

  return (
    <div className="flex gap-4 items-center py-2 xl:py-0">
      {!isSmallScreen &&
        quickFilters.map((filter) => {
          if (filter.filterType === 'multicheckbox') {
            return (
              <DropdownFilter
                key={filter.filterLabel}
                filterLabel={filter.filterLabel}
                filterOptions={filter.filterOptions}
                variant={filter.triggerProps?.variant}
                size={filter.triggerProps?.size}
                disabled={filter.triggerProps?.disabled}
                currentFiltersValues={currentFiltersValues}
                isSelected={isFilterSelected(
                  allFiltersValues,
                  currentFiltersValues,
                  currentFiltersData,
                  filter.filterLabel
                )}
                handleFiltersValuesChange={handleFiltersValuesChange}
                customItemRender={customItemRender}
              />
            );
          } else {
            return null;
          }
        })}
      {(!!additionalFilters.length || (!additionalFilters.length && isSmallScreen)) && (
        <AllFiltersDrawer
          handleOpenChange={handleDrawerOpenChange}
          isVisible={isDrawerVisible}
          allFilters={currentFiltersData}
          currentFiltersValues={currentFiltersValues}
          handleFiltersValuesChange={handleFiltersValuesChange}
          handleResetClick={handleResetClick}
          initialFiltersValues={initialFiltersValues}
          drawerCloseBtnText={drawerCloseBtnText}
          customItemRender={customItemRender}
        >
          <Button
            variant="outline"
            size="default"
            className={cn('px-4', isDrawerVisible ? '!ring-2 !ring-primary-600' : '')}
          >
            <div className="relative">
              {isAnyFilterSelected(isSmallScreen ? currentFiltersData : additionalFilters) && (
                <div className="absolute w-[9px] h-[9px] rounded-full bg-primary-600 top-0 right-[-1px] border-[1.5px] border-white" />
              )}
              <Icon icon={BarsFilterIcon} className="!w-5 !h-5" />
            </div>
            <span>All Filters</span>
          </Button>
        </AllFiltersDrawer>
      )}
      {!isInitialState(initialFiltersValues, currentFiltersValues) && (
        <div className="text-primary-600 font-semibold cursor-pointer hover:opacity-80" onClick={handleResetClick}>
          Reset
        </div>
      )}
    </div>
  );
};
